import { useState, useEffect, useRef, useCallback } from "react";
import {
  Phone, PhoneOff, Mic, MicOff, Volume2, Circle,
  FileText, ChevronUp, ChevronDown, Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { useCallRecorder } from "@/hooks/useCallRecorder";
import type { IncomingCall } from "@/hooks/useIncomingCalls";

interface IncomingCallModalProps {
  call: IncomingCall;
  callStatus: string;
  queueCount: number;
  onAccept: (sdpAnswer: string) => Promise<void>;
  onReject: () => void;
  onEnd: () => void;
  onDismiss: () => void;
}

export function IncomingCallModal({
  call,
  callStatus,
  queueCount,
  onAccept,
  onReject,
  onEnd,
  onDismiss,
}: IncomingCallModalProps) {
  const [muted, setMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ringtoneRef = useRef<AudioContext | null>(null);

  const { isRecording, startRecording, stopAndUpload, cancel: cancelRecording } =
    useCallRecorder();

  // Ringtone
  useEffect(() => {
    if (callStatus !== "ringing") return;
    try {
      const ctx = new AudioContext();
      ringtoneRef.current = ctx;
      const playTone = () => {
        if (!ringtoneRef.current || ringtoneRef.current.state === "closed") return;
        const osc = ringtoneRef.current.createOscillator();
        const gain = ringtoneRef.current.createGain();
        osc.connect(gain);
        gain.connect(ringtoneRef.current.destination);
        osc.frequency.value = 440;
        gain.gain.value = 0.15;
        osc.start();
        osc.stop(ringtoneRef.current.currentTime + 0.3);
        setTimeout(() => {
          if (!ringtoneRef.current || ringtoneRef.current.state === "closed") return;
          const osc2 = ringtoneRef.current.createOscillator();
          const gain2 = ringtoneRef.current.createGain();
          osc2.connect(gain2);
          gain2.connect(ringtoneRef.current.destination);
          osc2.frequency.value = 520;
          gain2.gain.value = 0.15;
          osc2.start();
          osc2.stop(ringtoneRef.current.currentTime + 0.3);
        }, 400);
      };
      playTone();
      const interval = setInterval(playTone, 2000);
      return () => {
        clearInterval(interval);
        ringtoneRef.current?.close();
        ringtoneRef.current = null;
      };
    } catch {}
  }, [callStatus]);

  useEffect(() => {
    if (callStatus !== "ringing") {
      ringtoneRef.current?.close();
      ringtoneRef.current = null;
    }
  }, [callStatus]);

  // Timer
  useEffect(() => {
    if (callStatus === "in_call") {
      setCallDuration(0);
      timerRef.current = setInterval(() => setCallDuration((d) => d + 1), 1000);
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [callStatus]);

  // Cleanup
  useEffect(() => {
    return () => {
      pcRef.current?.close();
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      if (timerRef.current) clearInterval(timerRef.current);
      ringtoneRef.current?.close();
    };
  }, []);

  const handleAccept = useCallback(async () => {
    if (!call.sdp_offer) {
      setError("No hay SDP offer disponible");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;

      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });
      pcRef.current = pc;

      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      pc.ontrack = (event) => {
        remoteStreamRef.current = event.streams[0];
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = event.streams[0];
          remoteAudioRef.current.play().catch(console.error);
        }
        // Start recording with both streams
        startRecording(stream, event.streams[0]);
      };

      pc.oniceconnectionstatechange = () => {
        if (pc.iceConnectionState === "failed" || pc.iceConnectionState === "disconnected") {
          setError("Conexión perdida");
        }
      };

      await pc.setRemoteDescription(
        new RTCSessionDescription({ type: "offer", sdp: call.sdp_offer })
      );

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      const sdpAnswer = await new Promise<string>((resolve) => {
        const timeout = setTimeout(() => {
          resolve(pc.localDescription?.sdp || answer.sdp || "");
        }, 3000);
        pc.onicegatheringstatechange = () => {
          if (pc.iceGatheringState === "complete") {
            clearTimeout(timeout);
            resolve(pc.localDescription?.sdp || answer.sdp || "");
          }
        };
      });

      await onAccept(sdpAnswer);
    } catch (err: any) {
      console.error("WebRTC setup error:", err);
      setError(err.message || "Error al conectar la llamada");
    }
  }, [call.sdp_offer, onAccept, startRecording]);

  const handleMuteToggle = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach((t) => {
        t.enabled = muted;
      });
      setMuted(!muted);
    }
  };

  const saveNotes = async () => {
    if (!notes.trim()) return;
    setSavingNotes(true);
    await supabase
      .from("calls")
      .update({ notes } as any)
      .eq("id", call.id);
    setSavingNotes(false);
  };

  const handleHangup = async () => {
    // Stop recording and upload
    if (call.id) {
      await stopAndUpload(call.id);
    }
    // Save notes
    if (notes.trim()) {
      await saveNotes();
    }

    pcRef.current?.close();
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    if (callStatus === "ringing") {
      onReject();
    } else {
      onEnd();
    }
  };

  const handleDismiss = async () => {
    if (notes.trim()) await saveNotes();
    cancelRecording();
    onDismiss();
  };

  const formatDuration = (s: number) =>
    `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

  const isOutbound = call.direction === "outbound";
  const contactName =
    call.contact_name || (isOutbound ? call.to_number : call.from_number) || "Desconocido";
  const displayPhone = isOutbound ? call.to_number : call.from_number;
  const initials = contactName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const isActive = callStatus === "in_call" || callStatus === "connecting";
  const isEnded = callStatus === "ended" || callStatus === "failed";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <audio ref={remoteAudioRef} autoPlay />
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-[380px] overflow-hidden">
        {/* Header gradient */}
        <div
          className={cn(
            "px-6 pt-6 pb-4 text-center transition-colors",
            callStatus === "ringing" && "bg-gradient-to-b from-primary/15 to-transparent",
            isActive && "bg-gradient-to-b from-emerald-500/10 to-transparent",
            isEnded && "bg-gradient-to-b from-muted/50 to-transparent"
          )}
        >
          {/* Queue badge */}
          {queueCount > 0 && (
            <div className="flex items-center justify-center gap-1.5 mb-3">
              <div className="bg-primary/10 text-primary text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5">
                <Users className="h-3 w-3" />
                {queueCount} en espera
              </div>
            </div>
          )}

          {/* Avatar */}
          <div className="mx-auto mb-3 relative">
            <div
              className={cn(
                "h-20 w-20 rounded-full flex items-center justify-center mx-auto",
                callStatus === "ringing" && "bg-primary/20 animate-pulse",
                isActive && "bg-emerald-500/20",
                isEnded && "bg-muted"
              )}
            >
              <span
                className={cn(
                  "text-2xl font-bold",
                  callStatus === "ringing" && "text-primary",
                  isActive && "text-emerald-600 dark:text-emerald-400",
                  isEnded && "text-muted-foreground"
                )}
              >
                {initials}
              </span>
            </div>
            {/* Recording indicator */}
            {isRecording && (
              <div className="absolute -top-1 -right-1 flex items-center gap-1 bg-destructive text-destructive-foreground text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                <Circle className="h-2 w-2 fill-current animate-pulse" />
                REC
              </div>
            )}
          </div>

          <h3 className="font-semibold text-lg">{contactName}</h3>
          {displayPhone && (
            <p className="text-sm text-muted-foreground mt-0.5">+{displayPhone}</p>
          )}

          {/* Status line */}
          <div className="mt-2">
            {callStatus === "ringing" && !isOutbound && (
              <p className="text-sm text-primary animate-pulse font-medium">
                📞 Llamada entrante...
              </p>
            )}
            {(callStatus === "calling" || callStatus === "initiating") && (
              <p className="text-sm text-primary animate-pulse font-medium">📞 Llamando...</p>
            )}
            {callStatus === "connecting" && (
              <p className="text-sm text-muted-foreground animate-pulse">Conectando...</p>
            )}
            {callStatus === "in_call" && (
              <div className="flex items-center justify-center gap-2">
                <Volume2 className="h-4 w-4 text-emerald-500 animate-pulse" />
                <span className="text-sm font-mono text-emerald-500 dark:text-emerald-400 font-semibold">
                  {formatDuration(callDuration)}
                </span>
              </div>
            )}
            {callStatus === "ended" && (
              <p className="text-sm text-muted-foreground">
                Llamada finalizada · {formatDuration(callDuration)}
              </p>
            )}
            {callStatus === "failed" && (
              <p className="text-sm text-destructive">Error en la conexión</p>
            )}
            {error && <p className="text-xs text-destructive mt-1">{error}</p>}
          </div>
        </div>

        {/* Controls */}
        <div className="px-6 pb-4 pt-1">
          {callStatus === "ringing" && !isOutbound && (
            <div className="flex items-center justify-center gap-6">
              <Button
                variant="destructive"
                size="icon"
                className="h-14 w-14 rounded-full"
                onClick={handleHangup}
              >
                <PhoneOff className="h-6 w-6" />
              </Button>
              <Button
                size="icon"
                className="h-14 w-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white"
                onClick={handleAccept}
              >
                <Phone className="h-6 w-6" />
              </Button>
            </div>
          )}

          {(callStatus === "calling" || callStatus === "initiating") && (
            <div className="flex items-center justify-center">
              <Button
                variant="destructive"
                size="icon"
                className="h-14 w-14 rounded-full"
                onClick={handleHangup}
              >
                <PhoneOff className="h-6 w-6" />
              </Button>
            </div>
          )}

          {isActive && (
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="outline"
                size="icon"
                className={cn(
                  "h-12 w-12 rounded-full",
                  muted && "bg-destructive/10 border-destructive text-destructive"
                )}
                onClick={handleMuteToggle}
              >
                {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </Button>
              <Button
                variant="destructive"
                size="icon"
                className="h-14 w-14 rounded-full"
                onClick={handleHangup}
              >
                <PhoneOff className="h-6 w-6" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className={cn(
                  "h-12 w-12 rounded-full",
                  showNotes && "bg-primary/10 border-primary text-primary"
                )}
                onClick={() => setShowNotes(!showNotes)}
              >
                <FileText className="h-5 w-5" />
              </Button>
            </div>
          )}

          {isEnded && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={handleDismiss}>
                Cerrar
              </Button>
            </div>
          )}
        </div>

        {/* Notes panel */}
        {(showNotes || isEnded) && (
          <div className="border-t border-border px-4 pb-4 pt-3">
            <button
              className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2 hover:text-foreground transition-colors"
              onClick={() => setShowNotes(!showNotes)}
            >
              <FileText className="h-3.5 w-3.5" />
              Notas de la llamada
              {showNotes ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronUp className="h-3 w-3" />
              )}
            </button>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Escribe notas sobre esta llamada..."
              className="min-h-[80px] text-sm resize-none bg-muted/50"
            />
            {notes.trim() && !isEnded && (
              <Button
                size="sm"
                variant="ghost"
                className="mt-1.5 text-xs h-7"
                onClick={saveNotes}
                disabled={savingNotes}
              >
                {savingNotes ? "Guardando..." : "Guardar notas"}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
